<template>
  <div class="promotion-banner" v-if="promotions.length > 0">
    <div
      class="banner-manager"
      v-for="promotion in promotions"
      :key="promotion.id"
      :class="bannerClasses(promotion)"
    >
      <img
        class="banner-background"
        v-if="hasBackground(promotion)"
        :src="promotion.backgroundUrl"
        alt=""
        @error="$set(backgroundErrors, promotion.id, true)"
      >
      <img
        class="banner-image"
        v-if="hasImage(promotion)"
        :src="promotion.imageUrl"
        alt=""
        @error="$set(imageErrors, promotion.id, true)"
      >
      <div class="banner-content">
        <div class="banner-eyebrow" v-if="promotion.badge || promotion.kicker">
          <span class="banner-badge" v-if="promotion.badge">{{ promotion.badge }}</span>
          <span class="banner-kicker" v-if="promotion.kicker">{{ promotion.kicker }}</span>
        </div>

        <div class="banner-title">{{ promotion.title }}</div>

        <p class="banner-subtitle" v-if="promotion.subtitle">{{ promotion.subtitle }}</p>

        <button class="banner-btn" @click="openDetailModal(promotion)">
          {{ promotion.buttonLabel }}
        </button>
      </div>
    </div>

    <dx-modal
      v-if="selected"
      :value="true"
      @input="closeDetailModal"
      id="modal-promotion-detail"
      size="lg">

      <div class="promo-hero">
        <span class="promo-hero-badge" v-if="selected.detail.badge">
          {{ selected.detail.badge }}
        </span>
        <h2 class="promo-hero-title">{{ selected.detail.title }}</h2>
        <span class="promo-hero-text" v-if="selected.detail.intro">
          {{ selected.detail.intro }}
        </span>
      </div>

      <div
        class="promo-content"
        v-if="selected.detail.content"
        v-html="selected.detail.content"
      ></div>

      <template v-if="selected.detail.videoUrl">
        <div class="promo-divider">
          <span class="promo-divider-label">Vídeo de explicação</span>
          <span class="promo-divider-line"></span>
        </div>

        <a
          class="promo-video"
          :href="selected.detail.videoUrl"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`Assistir ao vídeo de ${selected.detail.title} (abre em nova aba)`"
        >
          <img
            class="promo-video-cover"
            v-if="selected.detail.videoThumbnailUrl"
            :src="selected.detail.videoThumbnailUrl"
            alt=""
          >
          <span class="promo-video-play">
            <fa-icon :icon="['fas', 'play']"/>
          </span>
        </a>
      </template>

      <div class="promo-price" v-if="selected.detail.price">
        <div class="promo-price-line">
          <span class="promo-price-value">R$ {{ selected.detail.price.value | number }}</span>
          <span class="promo-price-unit" v-if="selected.detail.price.unit">
            {{ selected.detail.price.unit }}
          </span>
        </div>
        <span class="promo-price-text" v-if="selected.detail.price.description">
          {{ selected.detail.price.description }}
        </span>
      </div>

      <template v-if="selected.detail.benefits.length > 0">
        <div class="promo-divider">
          <span class="promo-divider-label">Por que usar</span>
          <span class="promo-divider-line"></span>
        </div>

        <ol class="promo-benefits">
          <li
            class="promo-benefit"
            v-for="(benefit, i) in selected.detail.benefits"
            :key="i"
          >
            <span class="promo-benefit-number" aria-hidden="true">{{ i + 1 }}</span>
            <div class="promo-benefit-content">
              <span class="promo-benefit-title">{{ benefit.title }}</span>
              <span class="promo-benefit-text">{{ benefit.text }}</span>
            </div>
          </li>
        </ol>
      </template>

      <template slot="footer">
        <button
          class="promo-btn promo-btn-outline"
          :disabled="saving"
          @click="closeDetailModal"
        >
          Agora não
        </button>
        <button
          class="promo-btn promo-btn-primary"
          :class="{loading: saving}"
          :disabled="saving"
          @click="registerInterest"
        >
          <fa-icon :icon="['fal', 'paper-plane']"/>
          {{ selected.detail.interestLabel }}
        </button>
      </template>
    </dx-modal>
  </div>
</template>

<script>
export default {
  name: 'PromotionBanner',
  data() {
    return {
      promotions: [],
      selected: null,
      backgroundErrors: {},
      imageErrors: {},
      saving: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      return this.$http.get('/promotions/active')
        .then(({ data }) => {
          this.promotions = data.items;
        })
        .catch(() => {});
    },
    hasBackground(promotion) {
      return !!promotion.backgroundUrl && !this.backgroundErrors[promotion.id];
    },
    hasImage(promotion) {
      return !!promotion.imageUrl && !this.imageErrors[promotion.id];
    },
    bannerClasses(promotion) {
      return {
        'banner-light': this.hasBackground(promotion) && promotion.backgroundTone === 'light',
        'banner-with-image': this.hasImage(promotion),
      };
    },
    openDetailModal(promotion) {
      this.selected = promotion;
    },
    closeDetailModal() {
      if (this.saving) return;

      this.selected = null;
    },
    registerInterest() {
      this.saving = true;

      this.$http.post(`/promotions/${this.selected.id}/interest`)
        .then(() => {
          this.$toast.success('Tudo certo, logo nosso time entrará em contato. Obrigado!');
          this.selected = null;
        })
        .catch(this.$toast.error)
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables";

.promotion-banner {
  .banner-manager {
    position: relative;
    display: flex;
    min-height: 150px;
    background: radial-gradient(120% 100% at 0% 0%, #00207c 0%, #3359c7 100%);
    border-radius: 20px;
    color: #ffffff;
    margin-bottom: 16px;
    overflow: hidden;
  }
  .banner-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .banner-image {
    position: absolute;
    top: 12%;
    right: 56px;
    height: 76%;
    max-width: 34%;
    object-fit: contain;
    object-position: right center;
  }
  .banner-content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    max-width: 760px;
    padding: 32px 36px;
  }
  .banner-eyebrow {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .banner-badge {
    background: #8fe3b8;
    border-radius: 50px;
    color: #0b3b2a;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1;
    padding: 5px 10px;
    text-transform: uppercase;
  }
  .banner-kicker {
    font-size: 13px;
    font-weight: 500;
    opacity: 0.85;
  }
  .banner-with-image .banner-content {
    max-width: 58%;
  }
  .banner-title {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.15;
    margin-bottom: 10px;
  }
  .banner-subtitle {
    font-size: 16px;
    margin: 0;
    opacity: 0.9;
  }
  .banner-btn {
    margin-top: 20px;
    background: #ffffff;
    border: none;
    border-radius: 10px;
    color: #00207c;
    font-size: 15px;
    font-weight: 700;
    padding: 10px 22px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.88;
    }
  }
  .banner-light {
    color: $dark-color;

    .banner-btn {
      background: radial-gradient(100% 99.92% at 0% 0%, #00207c 0%, #3359c7 100%);
      color: #ffffff;
    }
  }
  @media (max-width: 768px) {
    .banner-image {
      display: none;
    }
    .banner-content,
    .banner-with-image .banner-content {
      max-width: none;
      padding: 16px;
    }
    .banner-title {
      font-size: 20px;
    }
    .banner-btn {
      width: 100%;
    }
  }
}

#modal-promotion-detail {
  $promo-accent: #3d4db7;
  $promo-accent-bg: #eef0fa;
  $promo-accent-border: #c7ccee;
  $promo-text: #1a1e3e;
  $promo-text-label: #4b516c;
  $promo-text-muted: #62687f;
  $promo-text-faint: #9a9eae;
  $promo-border: #dadce5;
  $promo-border-strong: #c4c7d3;
  $promo-divider: #ebecf2;
  $promo-bg-subtle: #f3f4f9;
  $promo-bg-muted: #ebecf2;

  .modal.active .modal-overlay {
    background: rgba(31, 35, 64, 0.45);
  }
  .modal-container {
    max-width: 820px;
    max-height: 90vh;
    padding: 0;
    border-radius: 20px;
    box-shadow: 0 24px 48px rgba(31, 35, 64, 0.18);
  }
  .modal-header {
    display: none;
  }
  .modal-body {
    padding: 24px;
  }
  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin: 0 24px;
    padding: 16px 0 24px;
    border-top: 1px solid $promo-divider;
  }
  .promo-hero {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 24px 16px;
    background: linear-gradient(160deg, #ffffff, $promo-accent-bg);
    border: 1px solid $promo-accent-border;
    border-radius: 12px;
    text-align: center;
  }
  .promo-hero-badge {
    position: absolute;
    top: -8px;
    right: 12px;
    padding: 4px 8px;
    background: $promo-accent;
    border-radius: 999px;
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 13px;
    text-transform: uppercase;
  }
  .promo-hero-title {
    margin: 0;
    color: $promo-text;
    font-size: 17px;
    font-weight: 500;
    line-height: 22px;
  }
  .promo-hero-text {
    margin-top: 8px;
    color: $promo-text-muted;
    font-size: 14px;
    line-height: 20px;
    white-space: pre-line;
  }
  .promo-content {
    margin-top: 16px;
    padding: 16px;
    border: 1px solid $promo-border;
    border-radius: 12px;
    color: $promo-text;
    font-size: 14px;
    line-height: 20px;
    overflow-wrap: anywhere;

    p, ul, ol, table, h1, h2, h3, h4, h5, h6 {
      margin: 0 0 8px;
    }
    > :last-child {
      margin-bottom: 0;
    }
    h1, h2, h3 {
      font-size: 15px;
      font-weight: 500;
      line-height: 20px;
    }
    h4, h5, h6 {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
    strong, b {
      font-weight: 600;
    }
    ul, ol {
      padding-left: 20px;
    }
    ul {
      list-style: disc;
    }
    ol {
      list-style: decimal;
    }
    li {
      margin: 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    th, td {
      padding: 4px 8px;
      border: 1px solid $promo-border;
      text-align: left;
      vertical-align: top;
    }
    th {
      font-weight: 600;
    }
  }
  .promo-video {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 16 / 9;
    max-width: 240px;
    margin: 0 auto;
    background: $promo-bg-muted;
    border-radius: 12px;
    overflow: hidden;

    &:hover .promo-video-play {
      background: mix($promo-accent, $promo-text, 88%);
    }
  }
  .promo-video-cover {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .promo-video-play {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: $promo-accent;
    border-radius: 50%;
    box-shadow: 0 8px 24px rgba(31, 35, 64, 0.1);
    color: #ffffff;
    font-size: 14px;
    transition: background-color 0.15s;
  }
  .promo-price {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    margin-top: 16px;
    padding: 16px;
    border: 1px solid $promo-border;
    border-radius: 12px;
    text-align: center;
  }
  .promo-price-line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
  }
  .promo-price-value {
    color: $promo-text;
    font-size: 26px;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    letter-spacing: -0.01em;
    line-height: 32px;
  }
  .promo-price-unit {
    color: $promo-text-muted;
    font-size: 14px;
  }
  .promo-price-text {
    color: $promo-text-muted;
    font-size: 12px;
    line-height: 16px;
  }
  .promo-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 24px 0 16px;
  }
  .promo-divider-label {
    color: $promo-text-faint;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 13px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .promo-divider-line {
    flex: 1;
    height: 1px;
    background: $promo-divider;
  }
  .promo-benefits {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
  }
  .promo-benefit {
    display: flex;
    gap: 12px;
    margin: 0;
    padding: 16px 0;

    & + .promo-benefit {
      border-top: 1px solid $promo-border;
    }
  }
  .promo-benefit-number {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: $promo-accent-bg;
    border-radius: 50%;
    color: $promo-accent;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  }
  .promo-benefit-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .promo-benefit-title {
    color: $promo-text;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
  }
  .promo-benefit-text {
    color: $promo-text-muted;
    font-size: 14px;
    line-height: 20px;
    white-space: pre-line;
  }
  .promo-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border: 1px solid transparent;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s, border-color 0.15s;

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }
  .promo-btn-outline {
    background: #ffffff;
    border-color: $promo-border-strong;
    color: $promo-text-label;

    &:hover:not(:disabled) {
      background: $promo-bg-subtle;
    }
  }
  .promo-btn-primary {
    background: $promo-accent;
    border-color: $promo-accent;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: mix($promo-accent, $promo-text, 88%);
      border-color: mix($promo-accent, $promo-text, 88%);
    }
    &.loading::after {
      border-bottom-color: #ffffff;
      border-left-color: #ffffff;
    }
  }
  @media (max-width: 639px) {
    .modal-body {
      padding: 16px;
    }
    .modal-footer {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 8px;
      margin: 0 16px;
      padding: 16px 0;
    }
    .promo-btn {
      width: 100%;
    }
  }
}
</style>
